import React, { useContext, useEffect, useRef, useState } from "react";
import { Accordion, Alert, Button, Col, Form, Modal, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import Datetime from "react-datetime";
import { confirmAlert } from "react-confirm-alert";
import { ShowConfirm } from "./Alerts.js";
import EventRsvpWhatsAppAssociations from "./EventRsvpWhatsAppAssociations.js";
import EventRsvpAnswerCard from "./EventRsvpAnswerCard.js";
import RsvpHelp from "./RsvpHelp.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { getRsvpAnswers } from "../utils/rsvpAnswers.js";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import { format, isValid, parse } from "date-fns";
import { getEventRsvpSettings, putEventRsvpSettings } from "../api/rsvp.js";
import NotyfContext from "../contexts/NotyfContext.js";

import { normalizeSharedRsvpContent } from "../utils/rsvpPresentation.js";
import { RSVP_WHATSAPP_LANGUAGES, defaultRsvpWhatsAppFeedback, normalizeRsvpFeedbackLanguage } from "../utils/rsvpWhatsAppMessages.js";

const CONFIGURABLE_FEEDBACK_GROUPS = [
  {
    key: "confirmed",
    label: "Resposta com presença",
    description: "Exibida para uma resposta marcada como presença no evento, inclusive ao alterar ou repetir essa resposta.",
  },
  {
    key: "declined",
    label: "Resposta sem presença",
    description: "Exibida para uma resposta que não conta como presença no evento, inclusive ao alterar ou repetir essa resposta.",
  },
  {
    key: "closed",
    label: "Prazo encerrado",
    description: "Exibida quando o prazo deste RSVP terminou e não é mais possível responder.",
  },
];
const RSVP_DEADLINE_FORMAT = "dd/MM/yyyy HH:mm";

const normalizeApiDateTime = (value) => {
  const normalized = String(value || "").trim().replace(" ", "T");
  if (!normalized) return "";
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized) ? normalized : `${normalized}Z`;
};

const toLocalDateTime = (value, timeZone) => {
  if (!value) return "";
  const date = new Date(normalizeApiDateTime(value));
  return Number.isNaN(date.getTime()) ? "" : formatInTimeZone(date, timeZone, RSVP_DEADLINE_FORMAT);
};

const toIsoDateTime = (value, timeZone) => {
  if (!value) return null;
  const wallClock = String(value).trim();
  const parsedDate = parse(wallClock, RSVP_DEADLINE_FORMAT, new Date());
  if (!isValid(parsedDate) || format(parsedDate, RSVP_DEADLINE_FORMAT) !== wallClock) return null;
  const date = zonedTimeToUtc(format(parsedDate, "yyyy-MM-dd HH:mm:ss"), timeZone);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const registrationDeadline = (value, timeZone) => {
  const wallClock = String(value || "").trim();
  return toIsoDateTime(wallClock, timeZone) ? wallClock : toLocalDateTime(value, timeZone);
};

const makeDraft = (settings = {}, timeZone, registrationEndDate) => ({
  definition_id: settings.definition_id || "",
  name: settings.name || "Confirmação de presença",
  is_attendance: false,
  field_input_id: settings.field_input_id === null || settings.field_input_id === undefined ? "" : String(settings.field_input_id),
  answer_options: getRsvpAnswers(settings).map((item) => ({ ...item })),
  whatsapp_associations: (settings.whatsapp_associations || []).map((item) => ({ ...item, buttons: item.buttons.map((button) => ({ ...button })) })),
  deadline_at: toLocalDateTime(settings.deadline_at, timeZone) || registrationDeadline(registrationEndDate, timeZone),
  revision: settings.revision ?? null,
  source: settings.source || null,
  presentation: normalizeSharedRsvpContent(settings.presentation),
});

const RSVP_SETTINGS_ERROR_MESSAGES = {
  RSVP_WHATSAPP_ASSOCIATIONS_INVALID: "Associe cada botão de WhatsApp a uma resposta válida deste RSVP.",
  RSVP_WHATSAPP_TEMPLATE_CHANGED: "O modelo de WhatsApp mudou. Reabra esta janela e selecione a versão atual do modelo antes de salvar.",
  RSVP_ANSWERS_INVALID: "Preencha o texto e um valor único para cada resposta.",
  RSVP_ATTENDANCE_ANSWER_REQUIRED: "Marque ao menos uma resposta que conta como presença no evento.",
  RSVP_ATTENDANCE_REQUIRED: "O evento precisa ter um RSVP marcado como presença no evento. Marque esta opção ou selecione outro RSVP para essa função.",
  RSVP_STORAGE_MIGRATION_REQUIRED: "As respostas existentes deste evento precisam ser atualizadas antes de usar o RSVP.",
  RSVP_SNAPSHOT_INCOMPLETE: "Revise as opções e o prazo deste RSVP antes de continuar.",
  RSVP_FEEDBACK_INCOMPLETE: "Preencha o título ou a descrição de cada mensagem ao participante.",
  RSVP_SETTINGS_STALE: "A configuração mudou desde que você abriu esta janela. Suas alterações foram mantidas; carregue a versão atual para continuar.",
  RSVP_SETTINGS_LOCKED: "O campo ainda está vinculado a um RSVP. Altere a associação no RSVP antes de remover o campo.",
  RSVP_ANSWER_ID_RETIRED: "Uma opção removida não pode ser reutilizada. Adicione uma nova resposta.",
  RSVP_RESPONSE_UPDATE_POLICY_INVALID: "Escolha como tratar as respostas existentes ao alterar valores ou o campo.",
  RSVP_SETTINGS_CHOICES_INVALID: "Os valores das respostas precisam corresponder às opções válidas do campo associado ao RSVP.",
  RSVP_CHOICES_INVALID: "Os valores das respostas precisam corresponder às opções válidas do campo associado ao RSVP.",
  RSVP_SETTINGS_FIELD_INVALID: "O campo selecionado não pode ser usado para a confirmação de presença.",
  RSVP_FIELD_NOT_ALLOWED: "O campo selecionado não pode ser usado para a confirmação de presença.",
  RSVP_SETTINGS_DEADLINE_INVALID: "O prazo informado não é válido. Escolha uma data futura.",
  RSVP_SETTINGS_INCOMPLETE: "Complete a configuração de presença e escolha um prazo futuro.",
  RSVP_SETTINGS_EXPIRED: "O prazo deste RSVP terminou. Informe uma data futura para responder.",
  RSVP_DEFINITION_REQUIRED: "Selecione o RSVP que deseja editar ou abra a opção de adicionar um novo RSVP.",
  RSVP_DEFINITION_NOT_FOUND: "Este RSVP não está mais disponível. Reabra a lista de RSVPs do evento para continuar.",
  RSVP_SNAPSHOT_NOT_FOUND: "O servidor não conseguiu recuperar a versão deste RSVP. Tente salvar novamente em instantes.",
  RSVP_LEGACY_SNAPSHOT_REVIEW_REQUIRED: "A configuração antiga deste RSVP está incompleta ou contém valores conflitantes. Revise as respostas e o prazo antes de salvar.",
  RSVP_TEMPLATE_REFERENCE_INVALID: "Revise os botões do modelo e selecione um RSVP deste evento.",
  RSVP_TEMPLATE_CONTAINS_CREDENTIAL: "O modelo contém um link pessoal de participante. Use o placeholder de link RSVP gerado pelo editor.",
  RSVP_QUEUED_INVITATION_UNAVAILABLE: "O prazo ou a configuração deste RSVP não permite concluir o envio. Revise o prazo e as respostas.",
  RSVP_SETTINGS_INTERNAL_ERROR: "O servidor não conseguiu salvar este RSVP. Tente novamente em instantes. Suas alterações foram mantidas.",
  EVENT_ID_INVALID: "O evento informado não é válido. Reabra o evento antes de configurar o RSVP.",
  EVENT_NOT_FOUND: "Este evento não está mais disponível. Reabra a lista de eventos para continuar.",
  EVENT_ACCESS_DENIED: "Você não tem permissão para salvar configurações de RSVP neste evento.",
};

const getRsvpSettingsError = (error) => {
  const code = error?.response?.data?.code;
  if (RSVP_SETTINGS_ERROR_MESSAGES[code]) return RSVP_SETTINGS_ERROR_MESSAGES[code];
  const status = error?.response?.status;
  if (!error?.response) return "Não foi possível conectar ao servidor para salvar o RSVP. Verifique sua conexão e tente novamente.";
  if (status === 401) return "Sua sessão expirou. Entre novamente para salvar as configurações do RSVP.";
  if (status === 403) return RSVP_SETTINGS_ERROR_MESSAGES.EVENT_ACCESS_DENIED;
  if (status === 429) return "Muitas tentativas de salvar em pouco tempo. Aguarde um momento e tente novamente.";
  if (status >= 500) return RSVP_SETTINGS_ERROR_MESSAGES.RSVP_SETTINGS_INTERNAL_ERROR;
  const message = error?.response?.data?.message;
  return typeof message === "string" && message.trim() && !/^[A-Z][A-Z0-9_]+$/.test(message.trim())
    ? message
    : "Revise os campos, as respostas, as associações de WhatsApp e o prazo do RSVP antes de salvar.";
};

const escapeNotification = (message) => String(message).replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[character]));

const EventRsvpSettingsModal = ({ show, onHide, eventId, eventTimezone = "America/Sao_Paulo", registrationEndDate, onSaved, initialDefinitionId, nested = false }) => {
  const notyf = useContext(NotyfContext);
  const [draft, setDraft] = useState(() => makeDraft({}, eventTimezone, registrationEndDate));
  const [registry, setRegistry] = useState({ revision: 0, definitions: [], attendance_definition_id: null });
  const [whatsappTemplates, setWhatsAppTemplates] = useState([]);
  const [whatsappTemplatesAvailable, setWhatsAppTemplatesAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadSequence, setLoadSequence] = useState(0);
  const [draftDirty, setDraftDirty] = useState(false);
  const draftDirtyRef = useRef(draftDirty);
  draftDirtyRef.current = draftDirty;
  const [reloadConfirmed, setReloadConfirmed] = useState(false);
  const [revisionConflict, setRevisionConflict] = useState(false);
  const [activeFeedbackKey, setActiveFeedbackKey] = useState(null);
  const [feedbackChannel, setFeedbackChannel] = useState("page");
  const [feedbackLanguage, setFeedbackLanguage] = useState("pt_BR");
  const loadedEventIdRef = useRef(null);
  const otherDefinitions = registry.definitions.filter((item) => item.definition_id !== draft.definition_id);
  const isOnlyDefinition = otherDefinitions.length === 0;

  useEffect(() => {
    setActiveFeedbackKey(null);
  }, [show, eventId, draft.definition_id]);
  useEffect(() => {
    if (show) { setFeedbackChannel("page"); setFeedbackLanguage("pt_BR"); }
  }, [show, eventId, initialDefinitionId]);
  const feedbackLanguages = [...new Set([
    ...RSVP_WHATSAPP_LANGUAGES.map((item) => item.value),
    ...whatsappTemplates.map((item) => normalizeRsvpFeedbackLanguage(item.language)).filter(Boolean),
    ...Object.keys(draft.presentation.whatsapp.feedback_translations || {}),
  ])];

  useEffect(() => {
    if (!show || !eventId) return undefined;
    const preserveDraft = String(loadedEventIdRef.current) === String(eventId) && draftDirtyRef.current;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    setWhatsAppTemplates([]);
    getEventRsvpSettings({ eventId, definitionId: initialDefinitionId, includeWhatsAppTemplates: true, signal: controller.signal })
      .then((response) => {
        if (!active) return;
        const data = response.data || {};
        setWhatsAppTemplates(data.whatsapp_templates || []);
        setWhatsAppTemplatesAvailable(data.whatsapp_templates_available !== false);
        if (preserveDraft) return;
        const loadedRegistry = data.registry || { revision: 0, definitions: data.settings ? [data.settings] : [], attendance_definition_id: data.settings?.definition_id || null };
        setRegistry(loadedRegistry);
        const settings = initialDefinitionId ? data.settings || {} : {};
        setDraft({
          ...makeDraft(settings, eventTimezone, registrationEndDate),
          is_attendance: Boolean(initialDefinitionId && settings.definition_id === loadedRegistry.attendance_definition_id),
        });
        loadedEventIdRef.current = eventId;
        setDraftDirty(false);
      })
      .catch((requestError) => {
        if (active && requestError?.code !== "ERR_CANCELED") {
          setError(requestError?.response?.data?.message || "Não foi possível carregar a configuração de presença.");
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [eventId, eventTimezone, initialDefinitionId, loadSequence, registrationEndDate, show]);

  const update = (field, value) => {
    setDraftDirty(true);
    setDraft((current) => ({ ...current, [field]: value }));
  };
  const updateAnswer = (id, field, value) => {
    setDraftDirty(true);
    setDraft((current) => ({ ...current, answer_options: current.answer_options.map((answer) => answer.id === id ? { ...answer, [field]: value } : answer) }));
  };
  const updatePresentation = (section, field, value) => {
    setDraftDirty(true);
    setDraft((current) => ({
      ...current,
      presentation: {
        ...current.presentation,
        [section]: { ...current.presentation[section], [field]: value },
      },
    }));
  };
  const notifyValidation = (message) => notyf.open({ type: "warning", message: escapeNotification(message), duration: 7000, dismissible: true });

  const save = async () => {
    setError("");
    setRevisionConflict(false);
    const isAttendance = isOnlyDefinition || draft.is_attendance;
    if (!isAttendance && !otherDefinitions.some((item) => item.definition_id === registry.attendance_definition_id)) {
      notifyValidation(RSVP_SETTINGS_ERROR_MESSAGES.RSVP_ATTENDANCE_REQUIRED);
      return;
    }
    const answers = draft.answer_options.map((item) => ({ ...item, label: item.label.trim(), value: typeof item.value === "string" ? item.value.trim() : item.value }));
    const deadlineAt = toIsoDateTime(draft.deadline_at, eventTimezone);
    if (!draft.name.trim()) {
      notifyValidation("Informe um nome para este RSVP.");
      return;
    }
    if (!draft.presentation.prompt.desc.trim()) {
      notifyValidation("Preencha o texto de Confirmação da página.");
      return;
    }
    if (!answers.length) {
      notifyValidation("Adicione ao menos um botão de resposta.");
      return;
    }
    const incompleteAnswer = answers.findIndex((item) => !item.label || item.value === "" || item.value === null || item.value === undefined);
    if (incompleteAnswer !== -1) {
      notifyValidation(`Preencha o texto do botão e o valor registrado da Resposta ${incompleteAnswer + 1}.`);
      return;
    }
    const duplicateAnswer = answers.findIndex((item, index) => answers.findIndex((answer) => JSON.stringify(answer.value) === JSON.stringify(item.value)) !== index);
    if (duplicateAnswer !== -1) {
      notifyValidation(`O valor registrado da Resposta ${duplicateAnswer + 1} está repetido. Use um valor diferente para cada resposta.`);
      return;
    }
    if (isAttendance && !answers.some((item) => item.counts_as_attendance)) {
      notifyValidation(RSVP_SETTINGS_ERROR_MESSAGES.RSVP_ATTENDANCE_ANSWER_REQUIRED);
      return;
    }
    const invalidAssociation = draft.whatsapp_associations.some((association) => association.buttons.some((mapping) =>
      !answers.some((answer) => answer.id === mapping.option_id)));
    if (invalidAssociation) {
      notifyValidation(RSVP_SETTINGS_ERROR_MESSAGES.RSVP_WHATSAPP_ASSOCIATIONS_INVALID);
      return;
    }
    if (!deadlineAt) {
      notifyValidation("Informe um prazo válido no formato dd/MM/aaaa HH:mm.");
      return;
    }
    if (!draft.definition_id && new Date(deadlineAt).getTime() <= Date.now()) {
      notifyValidation("O prazo para responder já passou. Informe uma data futura.");
      return;
    }

    const incompleteFeedback = CONFIGURABLE_FEEDBACK_GROUPS.find(({ key }) => {
      const item = draft.presentation.feedback[key];
      return !item.title.trim() && !item.desc.trim();
    });
    if (incompleteFeedback) {
      notifyValidation(`Preencha o título ou a descrição de “${incompleteFeedback.label}” em Mensagens ao participante.`);
      setActiveFeedbackKey(incompleteFeedback.key);
      setFeedbackChannel("page");
      return;
    }
    for (const [language, feedback] of Object.entries(draft.presentation.whatsapp.feedback_translations || {})) {
      const incomplete = CONFIGURABLE_FEEDBACK_GROUPS.find(({ key }) => feedback[key] && !feedback[key].title.trim() && !feedback[key].desc.trim());
      if (incomplete) {
        notifyValidation(`Preencha o título ou a descrição de “${incomplete.label}” no idioma ${language}, ou desative a personalização.`);
        setFeedbackChannel("whatsapp"); setFeedbackLanguage(language); setActiveFeedbackKey(incomplete.key);
        return;
      }
    }

    setSaving(true);
    try {
      if (draft.definition_id) {
        const confirmed = await new Promise((resolve) => confirmAlert({
          closeOnEscape: false,
          closeOnClickOutside: false,
          afterClose: () => resolve(false),
          customUI: ({ onClose }) => <ShowConfirm
            title="Salvar alterações do RSVP?"
            description="As respostas existentes mantêm a opção escolhida, com os valores e a indicação de presença atualizados. Opções removidas exigem revisão; o histórico é preservado. Estas alterações passam a valer nos links já enviados e nos envios pendentes. Se uma resposta usada por uma campanha de WhatsApp for removida ou ficar sem associação válida, a campanha poderá ser pausada. Nesse caso, revise as associações e retome a campanha em Canais de comunicação. Mensagens já entregues mantêm seu texto e seus botões. Deseja continuar?"
            textConfirm="Salvar alterações"
            action={() => { resolve(true); onClose(); }}
            onClose={() => { resolve(false); onClose(); }}
          />,
        }));
        if (!confirmed) return;
      }
      const response = await putEventRsvpSettings({
        event_id: Number(eventId),
        definition_id: draft.definition_id || null,
        new_definition: !draft.definition_id,
        name: draft.name.trim(),
        is_attendance: isAttendance,
        field_input_id: draft.field_input_id || null,
        answer_options: answers,
        whatsapp_associations: draft.whatsapp_associations,
        deadline_at: deadlineAt,
        presentation: normalizeSharedRsvpContent(draft.presentation),
        expected_revision: draft.revision,
        expected_registry_revision: registry.revision,
      });
      const saved = response.data?.settings || response.data || {};
      const normalized = makeDraft(saved, eventTimezone);
      setDraft(normalized);
      loadedEventIdRef.current = eventId;
      setDraftDirty(false);
      onSaved?.(saved, response.data?.registry);
      onHide?.();
    } catch (requestError) {
      const code = requestError?.response?.data?.code;
      const hasRevisionConflict = code === "RSVP_SETTINGS_STALE";
      setRevisionConflict(hasRevisionConflict);
      setReloadConfirmed(false);
      const message = getRsvpSettingsError(requestError);
      const isValidationError = requestError?.response && requestError.response.status >= 400 && requestError.response.status < 500;
      notyf.open({ type: isValidationError ? "warning" : "danger", message: escapeNotification(message), duration: 7000, dismissible: true });
      if (hasRevisionConflict) setError(message);
    } finally {
      setSaving(false);
    }
  };

  const updateFeedback = (key, field, value) => {
    if (feedbackChannel === "whatsapp") {
      const whatsapp = draft.presentation.whatsapp;
      const translations = whatsapp.feedback_translations || {};
      const localized = translations[feedbackLanguage] || {};
      update("presentation", { ...draft.presentation, whatsapp: { ...whatsapp,
        feedback_translations: { ...translations, [feedbackLanguage]: { ...localized,
          [key]: { ...(localized[key] || defaultRsvpWhatsAppFeedback(feedbackLanguage, key)), [field]: value } } } } });
      return;
    }
    const feedback = { ...draft.presentation.feedback, [key]: { ...draft.presentation.feedback[key], [field]: value } };
    update("presentation", { ...draft.presentation, feedback });
  };
  const toggleCustomFeedback = (key, enabled) => {
    const whatsapp = draft.presentation.whatsapp;
    const translations = { ...whatsapp.feedback_translations };
    const localized = { ...translations[feedbackLanguage] };
    if (enabled) localized[key] = defaultRsvpWhatsAppFeedback(feedbackLanguage, key);
    else delete localized[key];
    translations[feedbackLanguage] = localized;
    update("presentation", { ...draft.presentation, whatsapp: { ...whatsapp, feedback_translations: translations } });
  };
  const renderFeedbackEditor = ({ key, label, description }) => {
    const isWhatsApp = feedbackChannel === "whatsapp";
    const custom = draft.presentation.whatsapp.feedback_translations?.[feedbackLanguage]?.[key];
    const feedback = isWhatsApp ? custom : draft.presentation.feedback[key];
    return (
    <Accordion.Item eventKey={key} key={key}>
      <Accordion.Header>{label}</Accordion.Header>
      <Accordion.Body>
        <p className="small text-muted mb-2">{description}</p>
        {isWhatsApp && <Form.Check className="mb-3" id={`rsvp-feedback-custom-${key}`} label="Personalizar esta mensagem" checked={Boolean(custom)} disabled={saving} onChange={(event) => toggleCustomFeedback(key, event.target.checked)} />}
        {isWhatsApp && !custom ? <p className="small text-muted mt-2 mb-0">Será usada a mensagem padrão no idioma do modelo enviado.</p> :
        <Row className="g-3">
          {[["title", "Título"], ["desc", "Descrição"], ["small", "Texto adicional"]].map(([field, fieldLabel]) => <Form.Group as={Col} md={field === "title" ? 12 : 6} key={field} controlId={`rsvp-feedback-${feedbackChannel}-${key}-${field}`}>
            <Form.Label>{fieldLabel}</Form.Label><Form.Control as={field === "title" ? "input" : "textarea"} maxLength={500} value={feedback[field]} disabled={saving} onChange={(event) => updateFeedback(key, field, event.target.value)} />
          </Form.Group>)}
        </Row>}
      </Accordion.Body>
    </Accordion.Item>
  );
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} size="lg" centered scrollable className={nested ? "nested-modal" : undefined} style={nested ? { zIndex: 1060 } : undefined} backdropClassName={nested ? "nested-modal-backdrop" : undefined}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{initialDefinitionId ? "Editar RSVP" : "Criar RSVP"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="p-2 d-flex align-items-center justify-content-between gap-2 flex-wrap"><span>{error}</span>{!draftDirty && <Button size="sm" variant="outline-danger" onClick={() => setLoadSequence((value) => value + 1)}>Tentar novamente</Button>}{revisionConflict && draftDirty && !reloadConfirmed && <Button size="sm" variant="outline-danger" onClick={() => setReloadConfirmed(true)}>Carregar configuração atual</Button>}</Alert>}
        {reloadConfirmed && revisionConflict && <Alert variant="secondary" className="p-2 d-flex align-items-center justify-content-between gap-2 flex-wrap"><span>Carregar a versão atual substituirá as alterações desta janela.</span><div className="d-flex gap-2"><Button size="sm" variant="outline-secondary" onClick={() => setReloadConfirmed(false)}>Manter alterações</Button><Button size="sm" variant="primary" onClick={() => { setError(""); setRevisionConflict(false); setReloadConfirmed(false); setDraftDirty(false); setLoadSequence((value) => value + 1); }}>Carregar agora</Button></div></Alert>}
        {loading ? <div className="text-center p-4"><Spinner animation="border" size="sm" className="me-2" />Carregando configuração...</div> : <>
          <Form.Group className="mb-3"><Form.Label>Nome do RSVP</Form.Label><Form.Control value={draft.name} maxLength={120} disabled={saving} onChange={(event) => update("name", event.target.value)} /></Form.Group>
          <Form.Check className="mb-2" label="Este RSVP define a presença no evento" checked={isOnlyDefinition || draft.is_attendance} disabled={saving || isOnlyDefinition} onChange={(event) => update("is_attendance", event.target.checked)} />
          <Form.Group className="mt-3 mb-4" controlId="rsvp-deadline">
            <Form.Label>Prazo para responder</Form.Label>
            <Datetime
              value={draft.deadline_at}
              onChange={(value) => update("deadline_at", typeof value === "string" ? value : value.format("DD/MM/YYYY HH:mm"))}
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              inputProps={{ id: "rsvp-deadline", placeholder: "dd/MM/aaaa HH:mm", className: "form-control", disabled: saving }}
              locale="pt-br"
            />
            {draft.definition_id && <Form.Text className="d-block mt-2 mb-0">Este prazo também vale para convites já enviados. Uma data passada encerra as respostas; uma data futura reabre convites expirados pelo prazo, sem reativar convites revogados.</Form.Text>}
          </Form.Group>
          <hr className="my-4" />
          <section className="px-1 py-2" aria-label="Página de confirmação do RSVP">
          <h6 className="mb-3">Página de confirmação<RsvpHelp id="rsvp-page-help" label="Página de confirmação" text="Conteúdo exibido ao abrir o link RSVP do e-mail ou de um botão com URL no WhatsApp." /></h6>
          <Row className="g-3">
            <Form.Group as={Col} xs={12} controlId="rsvp-confirmation">
              <Form.Label>Questão/Confirmação</Form.Label>
              <Form.Control as="textarea" rows={3} maxLength={500} value={draft.presentation.prompt.desc} disabled={saving} onChange={(event) => updatePresentation("prompt", "desc", event.target.value)} />
            </Form.Group>
            <Col xs={12}>
              <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                <Form.Label className="mb-0">Botões de resposta disponíveis<RsvpHelp id="rsvp-page-answers-help" label="Botões de resposta disponíveis" text="Estes botões aparecem na página de confirmação e não alteram o modelo do WhatsApp. O valor e a indicação de presença são compartilhados com os botões de WhatsApp associados abaixo." /></Form.Label>
                <Button size="sm" variant="outline-primary" disabled={saving || draft.answer_options.length >= 100} onClick={() => update("answer_options", [...draft.answer_options, { id: window.crypto?.randomUUID?.() || "answer_" + Date.now() + "_" + Math.random().toString(36).slice(2), label: "", value: "", counts_as_attendance: false }])}><FontAwesomeIcon icon={faPlus} /> Adicionar</Button>
              </div>
              {draft.answer_options.map((answer, index) => <EventRsvpAnswerCard
                key={answer.id} id={`rsvp-page-answer-${answer.id}`} title={`Resposta ${index + 1}`}
                textLabel="Texto do botão" answer={answer} disabled={saving}
                showAttendance={isOnlyDefinition || draft.is_attendance}
                onChange={(field, value) => updateAnswer(answer.id, field, value)}
                onRemove={() => update("answer_options", draft.answer_options.filter((item) => item.id !== answer.id))}
                removeDisabled={draft.answer_options.length === 1}
              />)}
            </Col>
          </Row>
          <Row className="g-3 mt-1">
            {[["title", "Título"], ["small", "Texto adicional"]].map(([key, label]) => <Form.Group as={Col} xs={12} key={key}>
              <Form.Label>{label}</Form.Label><Form.Control as={key === "title" ? "input" : "textarea"} maxLength={500} value={draft.presentation.prompt[key]} disabled={saving} onChange={(event) => updatePresentation("prompt", key, event.target.value)} />
            </Form.Group>)}
          </Row>
          </section>
          <hr className="my-4" />
          <EventRsvpWhatsAppAssociations templates={whatsappTemplates} associations={draft.whatsapp_associations} answers={draft.answer_options} disabled={saving} loading={loading} available={whatsappTemplatesAvailable} showAttendance={isOnlyDefinition || draft.is_attendance} onChange={(value) => update("whatsapp_associations", value)} onAnswerChange={updateAnswer} />
          <hr className="my-4" />
          <section className="px-1 py-2" aria-label="Mensagens ao participante">
            <h6 className="mb-3">Mensagens ao participante</h6>
            <p className="small text-muted mb-2">Respostas novas, alteradas ou repetidas usam a mesma mensagem conforme a opção escolhida. Avisos técnicos são traduzidos automaticamente.</p>
            <Tabs activeKey={feedbackChannel} onSelect={(key) => { setFeedbackChannel(key); setActiveFeedbackKey(null); }} className="mb-3" mountOnEnter unmountOnExit>
              <Tab eventKey="page" title="Página de confirmação">
                <p className="small text-muted mb-2">Mensagens exibidas na página acessada por um link RSVP do e-mail ou WhatsApp.</p>
                <Accordion activeKey={activeFeedbackKey} onSelect={setActiveFeedbackKey}>{CONFIGURABLE_FEEDBACK_GROUPS.map(renderFeedbackEditor)}</Accordion>
              </Tab>
              <Tab eventKey="whatsapp" title="WhatsApp">
                <Form.Group className="mb-3" controlId="rsvp-feedback-language">
                  <Form.Label>Idioma</Form.Label>
                  <Form.Select value={feedbackLanguage} disabled={saving} onChange={(event) => { setFeedbackLanguage(event.target.value); setActiveFeedbackKey(null); }}>
                    {feedbackLanguages.map((language) => <option key={language} value={language}>{RSVP_WHATSAPP_LANGUAGES.find((item) => item.value === language)?.label || language}</option>)}
                  </Form.Select>
                  <Form.Text className="d-block mt-2 mb-0">O retorno usa o idioma do modelo enviado. Sem personalização nesse idioma, será usada a mensagem padrão. Textos personalizados não são traduzidos automaticamente.</Form.Text>
                </Form.Group>
                <p className="small text-muted mb-2">A resposta selecionada será incluída automaticamente na confirmação enviada ao participante.</p>
                {!/^(pt|en|es)(_|$)/.test(feedbackLanguage) && <Alert variant="secondary" className="small p-2">Este idioma ainda não tem mensagens padrão traduzidas. O texto padrão será em inglês.</Alert>}
                <Accordion activeKey={activeFeedbackKey} onSelect={setActiveFeedbackKey}>{CONFIGURABLE_FEEDBACK_GROUPS.map(renderFeedbackEditor)}</Accordion>
              </Tab>
            </Tabs>
          </section>
        </>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancelar</Button>
        <Button variant="primary" onClick={save} disabled={loading || saving}>
          {saving && <Spinner animation="border" size="sm" className="me-2" />}
          Salvar configuração
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EventRsvpSettingsModal;
